/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView,
  ScrollView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Image,
  Alert,
  Switch,
  StatusBar,
  ActivityIndicator,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  companyProfile: `${API_BASE_URL}/api/data/company_profiles`,
  vendors: `${API_BASE_URL}/api/data/vendors`,
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const AddEditSubcontractorScreen = ({ navigation, route }) => {
  const { subcontractor, isEdit = false } = route.params || {};

  const [companyLogo, setCompanyLogo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    specialization: [],
    phone: '',
    email: '',
    location: '',
    teamSize: '',
    status: 'Active',
    ratings: {
      quality: 0,
      reliability: 0,
      cost: 0,
      time: 0,
    },
    notes: '',
    contractStartDate: '',
    contractEndDate: '',
    hourlyRate: '',
    emergencyContact: '',
  });

  const availableSpecializations = [
    'Concrete Work', 'Foundation', 'Structural', 'Electrical', 'Lighting',
    'Power Systems', 'Plumbing', 'Drainage', 'Water Systems', 'Drywall',
    'Plastering', 'Interior Finishing', 'Roofing', 'Waterproofing', 'Insulation',
    'Carpentry', 'Flooring', 'Painting', 'HVAC',
  ];

  const [selectedSpecializations, setSelectedSpecializations] = useState([]);

  useFocusEffect(
    useCallback(() => {
      loadCompanyLogo();
    }, [])
  );

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }
      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data?.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  useEffect(() => {
    if (isEdit && subcontractor) {
      setFormData({
        name: subcontractor.name || '',
        specialization: subcontractor.specialization || [],
        phone: subcontractor.contact?.phone || '',
        email: subcontractor.contact?.email || '',
        location: subcontractor.location || '',
        teamSize: subcontractor.teamSize || '',
        status: subcontractor.status || 'Active',
        ratings: subcontractor.ratings || { quality: 0, reliability: 0, cost: 0, time: 0 },
        notes: subcontractor.notes || '',
        contractStartDate: subcontractor.contractInfo?.startDate || '',
        contractEndDate: subcontractor.contractInfo?.endDate || '',
        hourlyRate: subcontractor.contractInfo?.hourlyRate || '',
        emergencyContact: subcontractor.contact?.emergencyPhone || '',
      });
      setSelectedSpecializations(subcontractor.specialization || []);
    }
  }, [isEdit, subcontractor]);

  const handleSpecializationToggle = (spec) => {
    const updated = selectedSpecializations.includes(spec)
      ? selectedSpecializations.filter(s => s !== spec)
      : [...selectedSpecializations, spec];
    setSelectedSpecializations(updated);
    setFormData(prev => ({ ...prev, specialization: updated }));
  };

  const handleRatingChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      ratings: { ...prev.ratings, [type]: parseFloat(value) || 0 },
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) { Alert.alert('Error', 'Company name is required'); return false; }
    if (!formData.email.trim()) { Alert.alert('Error', 'Email is required'); return false; }
    if (!formData.phone.trim()) { Alert.alert('Error', 'Phone number is required'); return false; }
    if (selectedSpecializations.length === 0) { Alert.alert('Error', 'At least one specialization is required'); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const companyId = await AsyncStorage.getItem('company_id');
      // Convert empty strings to null where appropriate to avoid NOT NULL issues
      const payload = {
        company_id: parseInt(companyId),
        company_name: formData.name.trim() || null,
        specialization: selectedSpecializations.length ? selectedSpecializations : null,
        contact_person: '', // could be added to form if needed
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        emergency_phone: formData.emergencyContact.trim() || null,
        address: formData.location.trim() || null,
        city: formData.location.split(',')[0]?.trim() || null,
        country: 'United Kingdom',
        team_size: formData.teamSize.trim() || null,
        status: formData.status,
        ratings: formData.ratings,
        overall: (formData.ratings.quality + formData.ratings.reliability + formData.ratings.cost + formData.ratings.time) / 4,
        notes: formData.notes.trim() || null,
        contract_info: {
          startDate: formData.contractStartDate.trim() || null,
          endDate: formData.contractEndDate.trim() || null,
          hourlyRate: formData.hourlyRate.trim() || null,
          contractType: 'Fixed Term',
        },
        type: 'subcontractor',
      };

      if (isEdit) {
        await api.put(`${API_ENDPOINTS.vendors}/${subcontractor.id}`, payload);
      } else {
        await api.post(API_ENDPOINTS.vendors, payload);
      }
      Alert.alert('Success', `Subcontractor ${isEdit ? 'updated' : 'added'} successfully!`, [
        { text: 'OK', onPress: () => navigation.goBack() },
      ]);
    } catch (error) {
      console.error('❌ Error saving subcontractor:', error);
      // Enhanced error logging
      if (error.response) {
        console.log('🔍 Server response data:', error.response.data);
        console.log('🔍 Server status:', error.response.status);
        console.log('🔍 Server headers:', error.response.headers);
        // Display the server error message if available
        const serverMsg = error.response.data?.error || error.response.data?.message || 'Unknown server error';
        Alert.alert('Error', `Server error: ${serverMsg}`);
      } else if (error.request) {
        console.log('🔍 No response received:', error.request);
        Alert.alert('Error', 'No response from server. Please check your connection.');
      } else {
        console.log('🔍 Error message:', error.message);
        Alert.alert('Error', `Request error: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    if (navigation.canGoBack()) navigation.goBack();
  };

  const renderRatingInput = (label, type) => (
    <View style={styles.ratingInputContainer}>
      <Text style={styles.ratingLabel}>{label}</Text>
      <TextInput
        style={styles.ratingInput}
        value={formData.ratings[type].toString()}
        onChangeText={(v) => handleRatingChange(type, v)}
        keyboardType="decimal-pad"
        placeholder="0.0"
        placeholderTextColor="#94A3B8"
        maxLength={3}
      />
      <Text style={styles.ratingMax}>/5.0</Text>
    </View>
  );

  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
        <Ionicons name="chevron-back" size={24} color="#fff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        {companyLogo ? (
          <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
        ) : (
          <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
        )}
        <Text style={styles.headerTitle}>
          <Text>{isEdit ? 'Edit Subcontractor' : 'Add Subcontractor'}</Text>
        </Text>
      </View>
      <TouchableOpacity style={styles.saveHeaderButton} onPress={handleSave} disabled={loading}>
        {loading ? <ActivityIndicator size="small" color="#fff" /> : <Ionicons name="checkmark" size={24} color="#fff" />}
      </TouchableOpacity>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      {renderHeader()}

      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Basic Information</Text>
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Company Name *</Text>
            <TextInput
              style={styles.textInput}
              value={formData.name}
              onChangeText={(t) => setFormData(p => ({ ...p, name: t }))}
              placeholder="Enter company name"
              placeholderTextColor="#94A3B8"
            />
          </View>
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Location *</Text>
            <TextInput
              style={styles.textInput}
              value={formData.location}
              onChangeText={(t) => setFormData(p => ({ ...p, location: t }))}
              placeholder="Enter location (e.g., London, UK)"
              placeholderTextColor="#94A3B8"
            />
          </View>
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Team Size</Text>
            <TextInput
              style={styles.textInput}
              value={formData.teamSize}
              onChangeText={(t) => setFormData(p => ({ ...p, teamSize: t }))}
              placeholder="e.g., 15-20 workers"
              placeholderTextColor="#94A3B8"
            />
          </View>
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Status</Text>
            <View style={styles.statusContainer}>
              <Text style={styles.statusLabel}>Active</Text>
              <Switch
                value={formData.status === 'Active'}
                onValueChange={(v) => setFormData(p => ({ ...p, status: v ? 'Active' : 'Inactive' }))}
                trackColor={{ false: '#64748B', true: '#0e4e96' }}
                thumbColor="#fff"
              />
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Contact Information</Text>
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Email *</Text>
            <TextInput
              style={styles.textInput}
              value={formData.email}
              onChangeText={(t) => setFormData(p => ({ ...p, email: t }))}
              placeholder="Enter email"
              placeholderTextColor="#94A3B8"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Phone Number *</Text>
            <TextInput
              style={styles.textInput}
              value={formData.phone}
              onChangeText={(t) => setFormData(p => ({ ...p, phone: t }))}
              placeholder="Enter phone"
              placeholderTextColor="#94A3B8"
              keyboardType="phone-pad"
            />
          </View>
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Emergency Contact</Text>
            <TextInput
              style={styles.textInput}
              value={formData.emergencyContact}
              onChangeText={(t) => setFormData(p => ({ ...p, emergencyContact: t }))}
              placeholder="Emergency number"
              placeholderTextColor="#94A3B8"
              keyboardType="phone-pad"
            />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Specializations *</Text>
          <Text style={styles.sectionSubtitle}>Select all that apply</Text>
          <View style={styles.specializationsGrid}>
            {availableSpecializations.map(spec => (
              <TouchableOpacity
                key={spec}
                style={[
                  styles.specializationChip,
                  selectedSpecializations.includes(spec) && styles.selectedSpecializationChip,
                ]}
                onPress={() => handleSpecializationToggle(spec)}
              >
                <Text
                  style={[
                    styles.specializationChipText,
                    selectedSpecializations.includes(spec) && styles.selectedSpecializationChipText,
                  ]}
                >
                  {spec}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Initial Ratings</Text>
          <Text style={styles.sectionSubtitle}>Set initial ratings (0.0 - 5.0)</Text>
          <View style={styles.ratingsContainer}>
            {renderRatingInput('Quality', 'quality')}
            {renderRatingInput('Reliability', 'reliability')}
            {renderRatingInput('Cost Effectiveness', 'cost')}
            {renderRatingInput('Time Management', 'time')}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Contract Information</Text>
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Hourly Rate</Text>
            <TextInput
              style={styles.textInput}
              value={formData.hourlyRate}
              onChangeText={(t) => setFormData(p => ({ ...p, hourlyRate: t }))}
              placeholder="e.g., £45/hour"
              placeholderTextColor="#94A3B8"
            />
          </View>
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Contract Start Date</Text>
            <TextInput
              style={styles.textInput}
              value={formData.contractStartDate}
              onChangeText={(t) => setFormData(p => ({ ...p, contractStartDate: t }))}
              placeholder="DD/MM/YYYY"
              placeholderTextColor="#94A3B8"
            />
          </View>
          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Contract End Date</Text>
            <TextInput
              style={styles.textInput}
              value={formData.contractEndDate}
              onChangeText={(t) => setFormData(p => ({ ...p, contractEndDate: t }))}
              placeholder="DD/MM/YYYY"
              placeholderTextColor="#94A3B8"
            />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Notes</Text>
          <TextInput
            style={styles.notesInput}
            value={formData.notes}
            onChangeText={(t) => setFormData(p => ({ ...p, notes: t }))}
            placeholder="Add any additional notes..."
            placeholderTextColor="#94A3B8"
            multiline
            numberOfLines={4}
            textAlignVertical="top"
          />
        </View>

        <View style={styles.actionButtons}>
          <TouchableOpacity style={styles.cancelButton} onPress={handleGoBack}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.saveButton} onPress={handleSave} disabled={loading}>
            {loading ? <ActivityIndicator size="small" color="#fff" /> : <Text style={styles.saveButtonText}>{isEdit ? 'Update' : 'Save'}</Text>}
          </TouchableOpacity>
        </View>
        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  // ... (styles unchanged, same as before)
  safeArea: { flex: 1, backgroundColor: '#4A90E2' },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: { padding: 5 },
  headerCenter: { flexDirection: 'row', alignItems: 'center' },
  headerLogo: { width: 30, height: 30, borderRadius: 15, resizeMode: 'cover' },
  headerTitle: { color: '#fff', fontSize: 20, fontWeight: 'bold', marginLeft: 10 },
  saveHeaderButton: { padding: 5 },
  container: { flex: 1, backgroundColor: '#f8fafc' },
  section: {
    backgroundColor: '#fff',
    marginHorizontal: 15,
    marginTop: 15,
    borderRadius: 12,
    padding: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  sectionTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a', marginBottom: 5 },
  sectionSubtitle: { fontSize: 14, color: '#64748B', marginBottom: 15 },
  inputGroup: { marginBottom: 15 },
  inputLabel: { fontSize: 14, fontWeight: '600', color: '#0f172a', marginBottom: 5 },
  textInput: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#0f172a',
    backgroundColor: '#f1f5f9',
  },
  statusContainer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 10 },
  statusLabel: { fontSize: 16, color: '#0f172a' },
  specializationsGrid: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 10 },
  specializationChip: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    backgroundColor: '#f1f5f9',
    marginRight: 8,
    marginBottom: 8,
  },
  selectedSpecializationChip: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  specializationChipText: { fontSize: 12, color: '#64748B' },
  selectedSpecializationChipText: { color: '#fff', fontWeight: '600' },
  ratingsContainer: { marginTop: 10 },
  ratingInputContainer: { flexDirection: 'row', alignItems: 'center', marginBottom: 15 },
  ratingLabel: { flex: 1, fontSize: 14, color: '#0f172a' },
  ratingInput: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    width: 60,
    textAlign: 'center',
    fontSize: 14,
    color: '#0f172a',
    backgroundColor: '#f1f5f9',
    marginRight: 5,
  },
  ratingMax: { fontSize: 14, color: '#64748B' },
  notesInput: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#0f172a',
    backgroundColor: '#f1f5f9',
    minHeight: 100,
    textAlignVertical: 'top',
  },
  actionButtons: { flexDirection: 'row', marginHorizontal: 15, marginTop: 20, gap: 10 },
  cancelButton: { flex: 1, backgroundColor: 'transparent', borderWidth: 1, borderColor: '#0e4e96', paddingVertical: 15, borderRadius: 8, alignItems: 'center' },
  cancelButtonText: { color: '#0e4e96', fontSize: 16, fontWeight: '600' },
  saveButton: { flex: 1, backgroundColor: '#fdb913', paddingVertical: 15, borderRadius: 8, alignItems: 'center' },
  saveButtonText: { color: '#fff', fontSize: 16, fontWeight: '600' },
  bottomPadding: { height: 30 },
});

export default AddEditSubcontractorScreen;